import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StepProps } from '../../types/onboarding';
import { BriefcaseIcon, BuildingIcon, ShieldIcon, CreditCardIcon, LandmarkIcon, TreesIcon, ArrowLeftRightIcon, HardHatIcon, Building2Icon, LinkIcon, HelpCircleIcon, HomeIcon, MinusIcon, PlusIcon } from 'lucide-react';
const LOAN_TYPES = [{
  id: 'Conventional',
  icon: BriefcaseIcon
}, {
  id: 'FHA',
  icon: BuildingIcon
}, {
  id: 'VA',
  icon: ShieldIcon
}, {
  id: 'HELOC',
  icon: CreditCardIcon
}, {
  id: 'Jumbo',
  icon: LandmarkIcon
}, {
  id: 'USDA',
  icon: TreesIcon
}, {
  id: 'Reverse Mortgage',
  icon: ArrowLeftRightIcon
}, {
  id: 'Construction',
  icon: HardHatIcon
}, {
  id: 'Commercial',
  icon: Building2Icon
}, {
  id: 'Bridge',
  icon: LinkIcon
}, {
  id: 'Non-QM',
  icon: HelpCircleIcon
}, {
  id: 'Home Equity',
  icon: HomeIcon
}];
export function LoanTypeCapacities({
  data,
  updateData,
  setCanAdvance
}: StepProps) {
  useEffect(() => {
    setCanAdvance(true);
  }, [setCanAdvance]);
  const capacities = data.loanTypeCapacities || {};
  const toggleLoanType = (id: string) => {
    const current = capacities[id];
    const isActive = current?.active || false;
    updateData({
      loanTypeCapacities: {
        ...capacities,
        [id]: {
          active: !isActive,
          dailyMaxLeads: current?.dailyMaxLeads || 5
        }
      }
    });
  };
  const updateMaxLeads = (id: string, delta: number) => {
    const current = capacities[id];
    if (!current || !current.active) return;
    const newLeads = Math.max(1, Math.min(50, current.dailyMaxLeads + delta));
    updateData({
      loanTypeCapacities: {
        ...capacities,
        [id]: {
          ...current,
          dailyMaxLeads: newLeads
        }
      }
    });
  };
  const activeCount = Object.values(capacities).filter(c => c.active).length;
  const totalCapacity = Object.values(capacities).reduce((sum, c) => c.active ? sum + c.dailyMaxLeads : sum, 0);
  return <div className="space-y-6" data-id="element-174">
      {/* Summary Bar */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-center justify-between shadow-sm" data-id="element-175">
        <div className="flex items-center space-x-2" data-id="element-176">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-177">
            <BriefcaseIcon className="w-4 h-4 text-blue-600" data-id="element-178" />
          </div>
          <div data-id="element-179">
            <h3 className="text-sm font-bold text-blue-900" data-id="element-180">
              Capacity Summary
            </h3>
            <p className="text-xs text-blue-700" data-id="element-181">
              <span className="font-semibold" data-id="element-182">{activeCount}</span> loan types
              active &middot;{' '}
              <span className="font-semibold" data-id="element-183">{totalCapacity}</span> leads/day
              total capacity
            </p>
          </div>
        </div>
      </div>

      {/* Grid of Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-184">
        {LOAN_TYPES.map(loanType => {
        const Icon = loanType.icon;
        const config = capacities[loanType.id];
        const isActive = config?.active || false;
        const dailyMaxLeads = config?.dailyMaxLeads || 5;
        return <div key={loanType.id} className={`flex flex-col p-4 rounded-xl border transition-all duration-200 ${isActive ? 'border-[#2563EB] bg-blue-50/30 shadow-md ring-1 ring-[#2563EB]/20' : 'border-gray-200 bg-white hover:border-gray-300 shadow-sm'}`} data-id="element-185">
              <div className="flex items-center justify-between" data-id="element-186">
                <div className="flex items-center space-x-3" data-id="element-187">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-[#059669]/10' : 'bg-gray-100'}`} data-id="element-188">
                    <Icon className={`w-5 h-5 transition-colors ${isActive ? 'text-[#059669]' : 'text-gray-400'}`} data-id="element-189" />
                  </div>
                  <span className={`text-sm font-bold transition-colors ${isActive ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-190">
                    {loanType.id}
                  </span>
                </div>

                <button type="button" onClick={() => toggleLoanType(loanType.id)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isActive ? 'bg-[#2563EB]' : 'bg-gray-200'}`} data-id="element-191">
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActive ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-192" />
                </button>
              </div>

              <AnimatePresence data-id="element-193">
                {isActive && <motion.div initial={{
              height: 0,
              opacity: 0,
              marginTop: 0
            }} animate={{
              height: 'auto',
              opacity: 1,
              marginTop: 16
            }} exit={{
              height: 0,
              opacity: 0,
              marginTop: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden" data-id="element-194">
                    <div className="pt-4 border-t border-blue-100/50" data-id="element-195">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" data-id="element-196">
                        Max leads per day
                      </label>
                      <div className="flex items-center h-9" data-id="element-197">
                        <button type="button" onClick={() => updateMaxLeads(loanType.id, -1)} disabled={dailyMaxLeads <= 1} className="flex items-center justify-center h-full px-3 border border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-198">
                          <MinusIcon className="w-4 h-4 text-gray-600" data-id="element-199" />
                        </button>
                        <div className="flex-1 flex items-center justify-center h-full border-y border-gray-300 bg-white text-sm font-bold text-gray-900" data-id="element-200">
                          {dailyMaxLeads}
                        </div>
                        <button type="button" onClick={() => updateMaxLeads(loanType.id, 1)} disabled={dailyMaxLeads >= 50} className="flex items-center justify-center h-full px-3 border border-gray-300 rounded-r-lg bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-201">
                          <PlusIcon className="w-4 h-4 text-gray-600" data-id="element-202" />
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </div>;
}